import React from 'react';
import { Popup } from "semantic-ui-react";

/**
 * Displays a list of practices with visual indicators for ramp status and custom rates
 * For multiple practices, shows the first one with a tooltip containing the full list
 * 
 * @param {Array} practicesArray - Array of practice identifiers
 * @param {Object} isRampApplicableForPractice - Mapping of practices on ramp
 * @param {Object} isProviderOwnerOfPractice - Mapping of practices where provider is owner
 * @param {string|number} rowId - Row identifier for lookups
 * @param {Object} isCustomRates - Mapping of practices with custom rates (underlined)
 * @param {Object} isHynContract - Mapping of practices with HYN contracts (italic)
 * @param {Function} getPracticeName - Function to get display name for practice
 */
const PracticesWithTooltip = ({
    practicesArray,
    isRampApplicableForPractice,
    isProviderOwnerOfPractice,
    rowId,
    isCustomRates,
    isHynContract,
    getPracticeName
}) => {
    if (!practicesArray || practicesArray.length === 0) {
        return null;
    }

    const getPracticeStyle = (practice) => ({
        className: isRampApplicableForPractice[rowId]?.[practice]
            ? (isProviderOwnerOfPractice[rowId]?.[practice]
                ? "boldRed" // Ramp practice and provider is owner
                : "orange") // Ramp practice but provider is not owner
            : "regularBlack", // Not a ramp practice
        style: {
            textDecoration: isCustomRates[practice] ? "underline" : "",
            fontStyle: isHynContract[practice?.toLowerCase()] ? 'italic' : ''
        }
    });

    // If only one practice, show it without tooltip
    if (practicesArray.length === 1) {
        const practice = practicesArray[0];
        const { className, style } = getPracticeStyle(practice);
        
        return (
            <span className={className} style={style}>
                {getPracticeName(practice)}
            </span>
        );
    }

    // For multiple practices, show first one + count with tooltip
    const firstPractice = practicesArray[0];
    const remainingCount = practicesArray.length - 1;
    const { className: firstClassName, style: firstStyle } = getPracticeStyle(firstPractice);

    const tooltipContent = (
        <div style={{ maxWidth: '300px' }}>
            {practicesArray.map((practice, index) => {
                const { className, style } = getPracticeStyle(practice);
                return (
                    <span key={index}>
                        <span className={className} style={style}>
                            {getPracticeName(practice)}
                        </span>
                        {index < practicesArray.length - 1 && ', '}
                    </span>
                );
            })}
        </div>
    );

    return (
        <Popup
            content={tooltipContent}
            trigger={
                <span style={{ cursor: 'pointer' }}>
                    <span className={firstClassName} style={firstStyle}>
                        {getPracticeName(firstPractice)}
                    </span>
                    <span className="regularBlack" style={{ marginLeft: '5px' }}>
                        +{remainingCount}
                    </span>
                </span>
            }
            position="top center"
            hoverable
        />
    );
};

export default PracticesWithTooltip;
